import type {
  TStaffAvailability,
  TStaffAwayReason,
  TStaffDirectoryEntry,
  TStaffFactor,
  TStaffPermission,
  TStaffSubRole,
} from '@/services/staff';
import type { TMatrixColumn } from '@/constants/adminManage';
import type { TMatrixGroupKey, TMatrixZoneKey } from '@/constants/permissionMatrix';

import {
  ADMIN_MANAGE_COPY,
  AVAILABILITY_LABEL,
  AWAY_REASON_OPTION_LABEL,
  MATRIX_COLUMNS,
  STAFF_CALENDAR_COPY,
  STAFF_SUB_ROLES,
  TOTP_COPY,
} from '@/constants/adminManage';
import { PERSONA_LABELS } from '@/constants/navigation';
import { GRANT_SETS, PERMISSIONS } from '@/constants/permissions';
import {
  MATRIX_GRANT_SEPARATOR,
  MATRIX_GROUPS,
  MATRIX_ZONES,
  PERMISSION_ZONE,
  SERVER_ACTION_ZONE,
} from '@/constants/permissionMatrix';
import { AVAILABILITIES, AWAY_REASONS } from '@/services/staff';
import { toAvailabilityLabel } from '@/utils/availability';
import { toDayMonth, toShortDate } from '@/utils/date';
import { toStaffDisplayName } from '@/utils/staffName';

import type { TComboboxOption } from 'components/ui/Combobox';
import type { TSelectOption } from 'components/ui/Select';

type TMatrixCell = 'allowed' | 'none';

type TMatrixRow = {
  key: string;
  label: string;
  caption: string;
  cells: TMatrixCell[];
};

type TMatrixSection = {
  key: TMatrixGroupKey;
  label: string;
  rows: TMatrixRow[];
};

const toRoleLabel = (subRole: TStaffSubRole | null): string => {
  if (subRole === null) {
    return ADMIN_MANAGE_COPY.blank;
  }

  return PERSONA_LABELS[subRole];
};

const toTotpLabel = (entry: TStaffDirectoryEntry, factors: ReadonlyMap<string, TStaffFactor> | null): string => {
  const factor = factors?.get(entry.userID);

  if (!factor) {
    return ADMIN_MANAGE_COPY.blank;
  }

  if (factor.totp === 'enrolled') {
    return TOTP_COPY.enrolled;
  }

  if (entry.lastActiveAt === null) {
    return TOTP_COPY.pending;
  }

  return TOTP_COPY.reset;
};

const toCalendarStateLabel = (entry: TStaffDirectoryEntry): string => {
  if (entry.hasCalendar) {
    return STAFF_CALENDAR_COPY.linked;
  }

  return STAFF_CALENDAR_COPY.notLinked;
};

const toLastActiveLabel = (value: string | null): string => {
  if (value === null) {
    return ADMIN_MANAGE_COPY.blank;
  }

  return toDayMonth(value);
};

const toFactorMap = (factors: readonly TStaffFactor[] | undefined): ReadonlyMap<string, TStaffFactor> | null => {
  if (factors === undefined) {
    return null;
  }

  return new Map(factors.map(factor => [factor.userID, factor]));
};

const toHeldGrants = (permissions: readonly TStaffPermission[], subRole: TStaffSubRole): ReadonlySet<string> => new Set([
  ...GRANT_SETS[subRole],
  ...permissions.filter(permission => permission.subRole === subRole).map(permission => permission.action),
]);

const holdsAny = (held: ReadonlySet<string>, grants: readonly string[]): boolean => grants.some(grant => held.has(grant));

const toMatrixCell = (permissions: readonly TStaffPermission[], grants: readonly string[], column: TMatrixColumn): TMatrixCell => {
  if (holdsAny(toHeldGrants(permissions, column.role), grants)) {
    return 'allowed';
  }

  return 'none';
};

const toMatrixRow = (permissions: readonly TStaffPermission[], key: string, label: string, grants: readonly string[]): TMatrixRow => ({
  key,
  label,
  caption: grants.join(MATRIX_GRANT_SEPARATOR),
  cells: MATRIX_COLUMNS.map(column => toMatrixCell(permissions, grants, column)),
});

const toZoneGrants = (zone: TMatrixZoneKey, serverActions: readonly string[]): string[] => [
  ...PERMISSIONS.filter(permission => PERMISSION_ZONE[permission] === zone),
  ...serverActions.filter(action => SERVER_ACTION_ZONE[action] === zone),
];

const toGroupRows = (permissions: readonly TStaffPermission[], serverActions: readonly string[], group: TMatrixGroupKey): TMatrixRow[] => {
  if (group === 'other') {
    return serverActions
      .filter(action => !Object.keys(SERVER_ACTION_ZONE).includes(action))
      .map(action => toMatrixRow(permissions, action, action, [action]));
  }

  return MATRIX_ZONES
    .filter(zone => zone.group === group)
    .map(zone => toMatrixRow(permissions, zone.key, zone.label, toZoneGrants(zone.key, serverActions)))
    .filter(row => row.caption !== '');
};

const toMatrixSections = (permissions: readonly TStaffPermission[]): TMatrixSection[] => {
  const serverActions = [...new Set(permissions.map(permission => permission.action))].sort();

  return MATRIX_GROUPS
    .map(group => ({ key: group.key, label: group.label, rows: toGroupRows(permissions, serverActions, group.key) }))
    .filter(section => section.rows.length > 0);
};

const toSubRoleOptions = (subRoles: readonly TStaffSubRole[]): TSelectOption[] => subRoles
  .map(subRole => ({ value: subRole, label: PERSONA_LABELS[subRole] }));

const toAvailabilityOptions = (): TSelectOption[] => AVAILABILITIES
  .map(availability => ({ value: availability, label: AVAILABILITY_LABEL[availability] }));

const toAwayReasonOptions = (): TSelectOption[] => AWAY_REASONS
  .map(reason => ({ value: reason, label: AWAY_REASON_OPTION_LABEL[reason] }));

const toStaffAvailability = (value: unknown): TStaffAvailability => AVAILABILITIES.find(key => key === value) ?? 'active';

const toStaffAwayReason = (value: unknown): TStaffAwayReason | null => AWAY_REASONS.find(key => key === value) ?? null;

const toStaffStatusLabel = (entry: TStaffDirectoryEntry): string => toAvailabilityLabel(entry) || AVAILABILITY_LABEL[entry.availability];

const toFirstSignInLabel = (createdAt: string | null): string => {
  if (createdAt === null) {
    return ADMIN_MANAGE_COPY.blank;
  }

  return toShortDate(createdAt);
};

const toStaffPickerOptions = (entries: readonly TStaffDirectoryEntry[]): TComboboxOption[] => entries.map(entry => ({
  value: entry.userID,
  label: toStaffDisplayName(entry.firstName, entry.lastName),
  caption: entry.email,
}));

const toAvailabilityChoices = (current: TStaffAvailability, isOffBlocked: boolean): TSelectOption[] => toAvailabilityOptions()
  .filter(option => !isOffBlocked || option.value !== 'inactive')
  .filter(option => current !== 'inactive' || option.value !== 'away');

const toRoleChoices = (isInactive: boolean): TSelectOption[] => toSubRoleOptions(STAFF_SUB_ROLES)
  .map(option => ({ ...option, isDisabled: isInactive && option.value === 'super_admin' }));

export type {
  TMatrixCell,
  TMatrixRow,
  TMatrixSection,
};
export {
  toAvailabilityChoices,
  toAvailabilityOptions,
  toAwayReasonOptions,
  toCalendarStateLabel,
  toFactorMap,
  toFirstSignInLabel,
  toLastActiveLabel,
  toMatrixSections,
  toRoleChoices,
  toRoleLabel,
  toStaffAvailability,
  toStaffAwayReason,
  toStaffPickerOptions,
  toStaffStatusLabel,
  toSubRoleOptions,
  toTotpLabel,
};
